import { useEffect, useState, useCallback } from "react";
import { api, downloadBlob } from "../api.js";
import SubmissionTimeline from "../components/SubmissionTimeline.jsx";
import ErrorBanner from "../components/ErrorBanner.jsx";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const monthLabel = (y, m) => `${MONTHS[m - 1]} ${y}`;

/**
 * Supervisor console.
 *
 * - Lists submissions awaiting my approval (default).
 * - Tap one to see the filled-in report (entries + totals + employee signature).
 * - Review -> type your name -> Approve. Approval:
 *     * re-generates the .xlsx with my signature in H49
 *     * locks the month
 *     * makes the finalized file downloadable.
 */
export default function SupervisorReview({ me }) {
  const [mode, setMode] = useState("queue"); // queue | detail
  const [showHistory, setShowHistory] = useState(false);
  const [queue, setQueue] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [err, setErr] = useState(null);

  // Admins see agency-wide pending reports. Supervisors only see reports
  // from workers assigned to them. The endpoint scopes this automatically
  // by role — we just change the UI copy + show the supervisor chip when
  // an admin is viewing someone else's queue.
  const isAdmin = me.role === "admin";

  const refreshQueue = useCallback(async () => {
    setErr(null);
    try {
      const d = await api.listPending({ history: showHistory });
      setQueue(d.submissions);
    } catch (e) {
      setErr(e.message);
    }
  }, [showHistory]);

  useEffect(() => {
    refreshQueue();
  }, [refreshQueue]);

  if (mode === "detail" && selectedId) {
    return (
      <SubmissionDetail
        submissionId={selectedId}
        me={me}
        onBack={() => {
          setSelectedId(null);
          setMode("queue");
          refreshQueue();
        }}
      />
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {isAdmin ? "Agency review" : "To review"}
          </h1>
          {isAdmin && (
            <p className="text-xs text-slate-500">
              Pending reports across every supervisor.
            </p>
          )}
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={showHistory}
            onChange={(e) => setShowHistory(e.target.checked)}
          />
          Show approved
        </label>
      </div>

      <ErrorBanner message={err} onRetry={refreshQueue} />

      {/* Loading vs load-failed-with-no-prior-data: if a fetch errored
          before we ever got data, `queue` is null AND `err` is set — don't
          also render "Loading queue…" forever. */}
      {queue === null && !err && (
        <div className="text-sm text-slate-500">Loading queue…</div>
      )}

      {queue && queue.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          {showHistory ? (
            <>
              No submissions yet — approved or pending.
              <br />
              Once workers submit reports, you'll see them here.
            </>
          ) : (
            <>
              Nothing to review right now.
              <br />
              Reports from your workers will show up here after they hit Submit.
              {/* When the queue is empty but history likely has rows, nudge
                  the reviewer toward the checkbox so they don't assume the
                  page is broken. */}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowHistory(true)}
                  className="text-xs font-medium text-endeavor-700 underline"
                >
                  Show past approvals
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <ul className="space-y-3">
        {queue?.map((s) => (
          <li key={s.id}>
            <button
              onClick={() => {
                setSelectedId(s.id);
                setMode("detail");
              }}
              className="card w-full text-left active:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {s.workerName}
                  </div>
                  <div className="text-xs text-slate-500">
                    {monthLabel(s.year, s.month)} · submitted{" "}
                    {s.submittedAt?.slice(0, 10)}
                  </div>
                  {isAdmin && s.supervisorName && (
                    <div className="mt-1 text-xs text-slate-500">
                      Supervisor:{" "}
                      <span className="font-medium text-slate-700">
                        {s.supervisorName}
                      </span>
                    </div>
                  )}
                  {isAdmin && !s.supervisorName && (
                    <div className="mt-1 text-xs text-amber-700">
                      ⚠ No supervisor assigned
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold tabular-nums text-slate-900">
                    ${s.totals.totalClaim.toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {s.totals.totalMiles.toFixed(1)} mi
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <StatusPill status={s.status} />
                <span className="text-sm text-endeavor-600">Review ›</span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "approved") {
    return (
      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
        Approved
      </span>
    );
  }
  if (status === "returned") {
    return (
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
        Returned
      </span>
    );
  }
  return (
    <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
      Awaiting review
    </span>
  );
}

function SubmissionDetail({ submissionId, me, onBack }) {
  const [data, setData] = useState(null);
  const [sig, setSig] = useState(me.fullName);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [justApproved, setJustApproved] = useState(false);

  // Return-to-worker modal state. Reason must be 3–1000 chars per server
  // validator — keeping that lower bound here avoids an extra roundtrip for
  // obvious typos.
  const [showReturn, setShowReturn] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [justReturned, setJustReturned] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      setData(await api.getSubmission(submissionId));
    } catch (e) {
      setErr(e.message);
    }
  }, [submissionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function approve() {
    if (!sig.trim()) {
      setErr("Type your name to sign as supervisor.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.approve(submissionId, sig.trim());
      setJustApproved(true);
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload() {
    setErr(null);
    setDownloading(true);
    try {
      const result = await api.downloadSubmission(submissionId);
      downloadBlob(result);
    } catch (e) {
      setErr(e.message);
    } finally {
      setDownloading(false);
    }
  }

  async function returnToWorker() {
    const reason = returnReason.trim();
    if (reason.length < 3) {
      setErr("Give the worker a reason (at least a few words) so they know what to fix.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.returnSubmission(submissionId, reason);
      setShowReturn(false);
      setJustReturned(true);
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <div className="p-4 text-sm text-slate-500">
        <button className="tap text-endeavor-600" onClick={onBack}>‹ Back</button>
        {/* If the fetch errored outright, don't keep saying "Loading…" —
            show the error with a retry button so a blip on the network
            doesn't require a full nav-back-then-reopen to recover. */}
        <div className="mt-4">
          {err ? (
            <ErrorBanner message={err} onRetry={refresh} />
          ) : (
            <div>Loading report…</div>
          )}
        </div>
      </div>
    );
  }

  const { submission, entries } = data;
  const isApproved = submission.status === "approved";
  const isReturned = submission.status === "returned";

  return (
    <div className="space-y-4 p-4">
      <button className="text-sm text-endeavor-600" onClick={onBack}>
        ‹ Back to queue
      </button>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">
          {monthLabel(submission.year, submission.month)}
        </div>
        <h1 className="text-xl font-semibold text-slate-900">
          {submission.workerName}
        </h1>
        <div className="text-xs text-slate-500">{submission.workerEmail}</div>
      </div>

      {justApproved && (
        <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Approved. The finalized form is available to download below.
        </div>
      )}

      {justReturned && (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Returned to {submission.workerName}. They'll see your reason and the
          month is now editable again.
        </div>
      )}

      {isReturned && !justReturned && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="font-semibold">Previously returned for revisions</div>
          <div className="mt-1 text-xs text-amber-800">
            Returned{" "}
            {submission.returnedAt?.slice(0, 10)} — waiting on{" "}
            {submission.workerName} to fix and resubmit.
          </div>
          {submission.returnedReason && (
            <div className="mt-2 whitespace-pre-wrap rounded bg-white/60 px-2 py-1 text-xs text-amber-900">
              {submission.returnedReason}
            </div>
          )}
        </div>
      )}

      {/* Totals */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-700">Claim summary</div>
        <dl className="mt-2 grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-500">Trips</dt>
          <dd className="text-right tabular-nums">{entries.length}</dd>
          <dt className="text-slate-500">Total miles</dt>
          <dd className="text-right tabular-nums">
            {submission.totals.totalMiles.toFixed(2)}
          </dd>
          <dt className="text-slate-500">Parking + Tolls</dt>
          <dd className="text-right tabular-nums">
            $
            {(
              submission.totals.totalParking + submission.totals.totalTolls
            ).toFixed(2)}
          </dd>
          <dt className="mt-2 border-t pt-1 font-semibold text-slate-800">
            Total Claim
          </dt>
          <dd className="mt-2 border-t pt-1 text-right font-semibold tabular-nums text-slate-900">
            ${submission.totals.totalClaim.toFixed(2)}
          </dd>
        </dl>
      </div>

      {/* Entry list — read-only */}
      <div className="card">
        <div className="mb-2 text-sm font-semibold text-slate-700">
          Trips ({entries.length})
        </div>
        <ul className="divide-y divide-slate-100">
          {entries.map((e) => (
            <li key={e.id} className="py-2 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-900">
                    {e.origin} → {e.destination}
                  </div>
                  <div className="text-xs text-slate-500">
                    {e.date} · {e.miles.toFixed(1)} mi
                    {e.parking > 0 && ` · $${e.parking.toFixed(2)} parking`}
                    {e.tolls > 0 && ` · $${e.tolls.toFixed(2)} tolls`}
                  </div>
                  {e.purpose && (
                    <div className="mt-0.5 text-xs italic text-slate-500">
                      {e.purpose}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Audit trail — who did what when. Server scopes by role so this
          is always safe to render for the supervisor / admin viewing their
          own queue. */}
      <SubmissionTimeline submissionId={submissionId} />

      {/* Employee attestation */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-700">
          Employee attestation
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {submission.workerName} certified this claim as actually and
          necessarily traveled on behalf of the Agency.
        </p>
        <div className="mt-2 flex items-baseline justify-between text-sm">
          <span className="font-semibold text-slate-800">
            {submission.employeeSignature}
          </span>
          <span className="text-xs text-slate-500">
            signed {submission.employeeSigDate}
          </span>
        </div>
      </div>

      {/* Preview/download — fetch with the x-endeavor-user header and
          trigger a browser download from the blob. A plain <a> link would
          open a new tab without the header and get a 401 from the server. */}
      <button
        type="button"
        className="btn-secondary w-full"
        onClick={handleDownload}
        disabled={downloading}
      >
        {downloading
          ? "Generating…"
          : isApproved
          ? "Download final .xlsx"
          : "Preview the filled-in form (.xlsx)"}
      </button>

      {/* Approval block — hidden once the report has been approved or
          returned (returned reports are waiting on the worker to resubmit,
          not another supervisor pass). */}
      {!isApproved && !isReturned ? (
        <>
          <div>
            <label className="label">Sign as supervisor</label>
            <input
              className="field font-semibold"
              value={sig}
              onChange={(e) => setSig(e.target.value)}
            />
          </div>
          <ErrorBanner message={err} />
          <button
            className="btn-primary w-full"
            onClick={approve}
            disabled={busy}
          >
            {busy ? "Approving…" : "Approve & counter-sign"}
          </button>
          <button
            className="btn-secondary w-full"
            onClick={() => {
              setErr(null);
              setReturnReason("");
              setShowReturn(true);
            }}
            disabled={busy}
          >
            Return for revisions…
          </button>
          <p className="text-center text-xs text-slate-500">
            Approving locks the month for the employee and produces the final
            .xlsx. Returning reopens the month so they can edit and resubmit.
          </p>
        </>
      ) : isApproved ? (
        <div className="card">
          <div className="text-sm font-semibold text-slate-700">
            Supervisor signature
          </div>
          <div className="mt-2 flex items-baseline justify-between text-sm">
            <span className="font-semibold text-slate-800">
              {submission.supervisorSignature}
            </span>
            <span className="text-xs text-slate-500">
              signed {submission.supervisorSigDate}
            </span>
          </div>
        </div>
      ) : null}

      {/* Return-to-worker modal. Kept inline (no portal) for simplicity;
          mobile PWA context means there's no z-index competition. */}
      {showReturn && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-slate-900/40 p-4 sm:items-center">
          <div className="w-full max-w-md rounded-xl bg-white p-4 shadow-xl">
            <h2 className="text-base font-semibold text-slate-900">
              Return report for revisions
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              The worker will be notified with the reason below. Be specific so
              they can fix the right thing and resubmit.
            </p>
            <textarea
              className="field mt-3 min-h-[100px] font-normal"
              placeholder="e.g. Trip on the 14th looks duplicated with the 13th — please delete one or add a note."
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              maxLength={1000}
              autoFocus
            />
            <div className="mt-1 text-right text-[10px] text-slate-400">
              {returnReason.length}/1000
            </div>
            <div className="mt-2">
              <ErrorBanner message={err} />
            </div>
            <div className="mt-3 flex gap-2">
              <button
                className="btn-secondary flex-1"
                onClick={() => {
                  setShowReturn(false);
                  setErr(null);
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                className="btn-primary flex-1"
                onClick={returnToWorker}
                disabled={busy}
              >
                {busy ? "Returning…" : "Return to worker"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
